import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'

import { type ToastOptions, useToast } from '@hrworks/sui-core/Toast'

import { registerGlobalErrorHandler, unregisterGlobalErrorHandler } from '../../lib/notifications/globalNotification'

export interface AddNotificationProps {
  message: ReactNode
  title?: string
  color?: ToastOptions['color']
  /** Milliseconds until the toast closes itself; 'infinite' keeps it until dismissed. */
  duration?: number | 'infinite'
}

interface NotificationContextValue {
  addNotification: (props: AddNotificationProps) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

// Toasts render through the ToastProvider that SuiProvider mounts, so this provider must sit inside SuiProvider.
export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const toast = useToast()

  const addNotification = useCallback(
    ({ message, title, color, duration }: AddNotificationProps) => {
      toast.add({
        title,
        description: message,
        color,
        timeout: duration === 'infinite' ? 0 : duration,
      })
    },
    [toast]
  )

  useEffect(() => {
    registerGlobalErrorHandler((message) => {
      addNotification({ message, color: 'danger', duration: 5000 })
    })
    return () => unregisterGlobalErrorHandler()
  }, [addNotification])

  const value = useMemo(() => ({ addNotification }), [addNotification])

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export const useNotifications = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return ctx
}
